import React, { useRef, useState } from "react";

import Tab from "./Tab";
import ZestComponent from "./ZestComponent";
import { slugify } from "./slugify";
import type { TabDropdownOption } from "./Tab";

// React rebuild of the ZEST v2 Figma component set Zest::Tabs / Zest::Tab

export interface TabConfig {
  name: string;
  badgeText?: string;
  href?: string | null;
  dropdownOptions?: TabDropdownOption[];
  defaultOptionIndex?: number;
  content?: React.ReactNode;
  src?: string;
  frameId?: string;
}

export interface TabsProps {
  tabs: TabConfig[];
  defaultActiveIndex?: number;
  onDropdownChange?: (tabIndex: number, optionIndex: number, option: TabDropdownOption) => void;
  onTabChange?: (index: number) => void;
  children?: React.ReactNode;
}

type SelectedOptionByTab = Record<number, number>;

const defaultFrameId = (name: string): string => `zest-tab-frame-${slugify(name)}`;

const tabElementId = (index: number): string => `zest-tab-${index}`;
const tabPanelId = (index: number): string => `zest-tabpanel-${index}`;

const tabHasPanel = (tab: TabConfig): boolean =>
  Boolean(tab.content || tab.src || tab.dropdownOptions?.some((option) => option.content || option.src));

const initialSelectedOptionByTab = (tabs: TabConfig[]): SelectedOptionByTab =>
  tabs.reduce<SelectedOptionByTab>((selected, tab, index) => {
    if (tab.defaultOptionIndex) selected[index] = tab.defaultOptionIndex;
    return selected;
  }, {});

// `content` may be an HTML string when passed from ERB (a rendered partial) or
// a real React node when Tabs is composed directly in another React component.
const isHtmlString = (content: React.ReactNode): content is string => typeof content === "string";

const Tabs = ({ tabs, defaultActiveIndex = 0, onDropdownChange, onTabChange, children }: TabsProps): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
  const [selectedOptionByTab, setSelectedOptionByTab] = useState<SelectedOptionByTab>(() =>
    initialSelectedOptionByTab(tabs)
  );
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  const tablistRef = useRef<HTMLDivElement | null>(null);

  const selectTab = (index: number): void => {
    const wasActive = index === activeIndex;
    setActiveIndex(index);
    if (!wasActive) onTabChange?.(index);
    const hasDropdown = Boolean(tabs[index]?.dropdownOptions?.length);

    // Selecting an inactive tab only activates it — the dropdown flyout
    // toggles on clicks to the already-active tab.
    if (!hasDropdown || !wasActive) {
      setOpenDropdownIndex(null);
      return;
    }

    setOpenDropdownIndex((current) => (current === index ? null : index));
  };

  const selectOption = (tabIndex: number, optionIndex: number): void => {
    setSelectedOptionByTab((current) => ({ ...current, [tabIndex]: optionIndex }));
    setOpenDropdownIndex(null);
    const option = tabs[tabIndex]?.dropdownOptions?.[optionIndex];
    if (option) onDropdownChange?.(tabIndex, optionIndex, option);
  };

  // Automatic-activation tabs pattern: Left/Right move between tabs, Home/End
  // jump to the first/last. Moving focus also activates the tab, matching
  // the existing click-to-activate model.
  const handleTablistKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

    event.preventDefault();
    const tabButtons = Array.from(tablistRef.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []);
    if (!tabButtons.length) return;

    let nextIndex: number;
    if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = tabs.length - 1;
    else {
      const delta = event.key === "ArrowRight" ? 1 : -1;
      nextIndex = (activeIndex + delta + tabs.length) % tabs.length;
    }

    selectTab(nextIndex);
    tabButtons[nextIndex]?.focus();
  };

  const activeTab = tabs[activeIndex];
  const activeOption = activeTab?.dropdownOptions?.[selectedOptionByTab[activeIndex] || 0];
  const activeContent = activeOption?.content ?? activeTab?.content;
  const activeSrc = activeOption?.src ?? activeTab?.src;
  const activeFrameId =
    activeOption?.frameId ??
    activeTab?.frameId ??
    (activeSrc ? defaultFrameId(activeOption?.name ?? activeTab?.name ?? "") : undefined);
  const hasPanels = tabs.some(tabHasPanel);
  const activeTabId = tabElementId(activeIndex);
  const activePanelId = tabPanelId(activeIndex);

  return (
    <ZestComponent name="tabs">
      <div
        ref={tablistRef}
        role="tablist"
        aria-orientation="horizontal"
        onKeyDown={handleTablistKeyDown}
        className="flex gap-6 overflow-auto snap-x snap-mandatory"
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.name}
            id={tabElementId(index)}
            panelId={index === activeIndex && tabHasPanel(tab) ? tabPanelId(index) : undefined}
            name={tab.name}
            badgeText={tab.badgeText}
            active={index === activeIndex}
            href={tab.href}
            dropdownOptions={tab.dropdownOptions}
            selectedOptionIndex={selectedOptionByTab[index] || 0}
            dropdownOpen={openDropdownIndex === index}
            onSelect={() => selectTab(index)}
            onSelectOption={(optionIndex) => selectOption(index, optionIndex)}
            onRequestClose={() => setOpenDropdownIndex(null)}
          />
        ))}
      </div>
      {children && <div className="pt-8">{children}</div>}
      {hasPanels && activeSrc && (
        // Mirrors a plain <turbo-frame src="..."> — Turbo (already loaded globally) fetches
        // `src` and swaps in whichever element in that response has a matching `id`. Keyed by
        // frame id so switching between src-backed tabs mounts a fresh frame rather than
        // patching `src` on the same node.
        <div className="pt-4" role="tabpanel" id={activePanelId} aria-labelledby={activeTabId}>
          <turbo-frame key={activeFrameId} id={activeFrameId} src={activeSrc}>
            Loading…
          </turbo-frame>
        </div>
      )}
      {hasPanels &&
        !activeSrc &&
        activeContent &&
        (isHtmlString(activeContent) ? (
          <div
            className="pt-8"
            role="tabpanel"
            id={activePanelId}
            aria-labelledby={activeTabId}
            dangerouslySetInnerHTML={{ __html: activeContent }}
          />
        ) : (
          <div className="pt-8" role="tabpanel" id={activePanelId} aria-labelledby={activeTabId}>
            {activeContent}
          </div>
        ))}
    </ZestComponent>
  );
};

export default Tabs;
